// src/services/partenaireskataServices.js

import axios from "axios";

/**
 * URL du serveur MonAppliClub.
 *
 * Dans le fichier .env de la Ligue :
 *
 * REACT_APP_PKA_API_URL=https://api.monappliclub.com
 *
 * La variable doit contenir la racine du serveur.
 * Le service ajoute automatiquement /api/partenaireskata.
 */
const RAW_BASE_ROOT =
  process.env.REACT_APP_PKA_API_URL ||
  "https://api.monappliclub.com";

/**
 * Accepte les deux formats suivants :
 *
 * https://api.monappliclub.com
 * https://api.monappliclub.com/api/partenaireskata
 */
const createApiBaseUrl = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .replace(/\/+$/, "");

  if (normalizedValue.endsWith("/api/partenaireskata")) {
    return normalizedValue;
  }

  return `${normalizedValue}/api/partenaireskata`;
};

const API_BASE_URL = createApiBaseUrl(RAW_BASE_ROOT);

/**
 * Instance dédiée à l'API MonAppliClub.
 *
 * Aucun token Ligue n'est envoyé :
 * - les lectures sont publiques ;
 * - les modifications sont sécurisées par email + mot de passe ;
 * - envoyer le token d'un autre site peut provoquer un preflight CORS inutile.
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,

  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

/* =========================================================
   HELPERS
========================================================= */

const normalizeDepartement = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value)
    .trim()
    .toUpperCase();

  if (/^\d$/.test(normalizedValue)) {
    return normalizedValue.padStart(2, "0");
  }

  return normalizedValue;
};

const cleanParams = (params = {}) => {
  return Object.entries(params).reduce(
    (cleanedParams, [key, value]) => {
      if (
        value === undefined ||
        value === null ||
        value === ""
      ) {
        return cleanedParams;
      }

      if (key === "departement") {
        cleanedParams[key] = normalizeDepartement(value);
        return cleanedParams;
      }

      cleanedParams[key] = value;
      return cleanedParams;
    },
    {},
  );
};

const createApiError = (error) => {
  const responseData = error?.response?.data;

  let message =
    responseData?.message ||
    error?.message ||
    "Une erreur inattendue est survenue.";

  if (
    Array.isArray(responseData?.errors) &&
    responseData.errors.length > 0
  ) {
    message = responseData.errors.join(" ");
  }

  const apiError = new Error(message);

  apiError.status =
    error?.response?.status || null;

  apiError.errors = Array.isArray(responseData?.errors)
    ? responseData.errors
    : [];

  apiError.responseData = responseData || null;

  return apiError;
};

const handleApiError = (error) => {
  const normalizedError = createApiError(error);

  console.error(
    "[Partenaires Kata API]",
    normalizedError.message,
    {
      status: normalizedError.status,
      responseData: normalizedError.responseData,
    },
  );

  throw normalizedError;
};

const normalizeListResponse = (responseData) => {
  /**
   * Compatibilité temporaire avec une éventuelle ancienne API
   * qui renverrait directement un tableau.
   */
  if (Array.isArray(responseData)) {
    return {
      success: true,

      data: responseData,

      meta: {
        page: 1,
        limit: responseData.length,
        total: responseData.length,
        total_pages: 1,
      },

      department_counts: {},
    };
  }

  const rows = Array.isArray(responseData?.data)
    ? responseData.data
    : [];

  return {
    success: responseData?.success !== false,

    data: rows,

    meta: {
      page: Number(responseData?.meta?.page) || 1,

      limit:
        Number(responseData?.meta?.limit) ||
        rows.length,

      total:
        Number(responseData?.meta?.total) ||
        rows.length,

      total_pages:
        Number(responseData?.meta?.total_pages) || 1,
    },

    department_counts:
      responseData?.department_counts &&
      typeof responseData.department_counts === "object"
        ? responseData.department_counts
        : {},
  };
};

/* =========================================================
   API PRINCIPALE — MÊMES NOMS QUE SUR MONAPPLICLUB
========================================================= */

/**
 * Retourne une page de partenaires.
 *
 * Paramètres possibles :
 *
 * {
 *   page: 1,
 *   limit: 12,
 *   q: "",
 *   region: "",
 *   departement: "",
 *   passage_grade: "",
 *   kata: "",
 *   role_recherche: "",
 *   sort: "newest" | "oldest" | "name" | "grade"
 * }
 *
 * Retour :
 *
 * {
 *   success,
 *   data,
 *   meta,
 *   department_counts
 * }
 */
export const getPartenairesKata = async (
  params = {},
) => {
  try {
    const response = await api.get("", {
      params: cleanParams(params),
    });

    return normalizeListResponse(response.data);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Statistiques générales.
 *
 * Retour :
 *
 * {
 *   success: true,
 *   data: {
 *     total,
 *     regions,
 *     departements,
 *     recent
 *   }
 * }
 */
export const getPartenairesKataStats = async () => {
  try {
    const response = await api.get("/stats");

    return {
      success: response.data?.success !== false,

      data: {
        total:
          Number(response.data?.data?.total) || 0,

        regions:
          Number(response.data?.data?.regions) || 0,

        departements:
          Number(
            response.data?.data?.departements,
          ) || 0,

        recent:
          Number(response.data?.data?.recent) || 0,
      },
    };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Récupère une annonce par son identifiant.
 */
export const getPartenaireKataById = async (
  partenaireId,
) => {
  if (!partenaireId) {
    throw new Error(
      "L'identifiant du partenaire est obligatoire.",
    );
  }

  try {
    const response = await api.get(
      `/${partenaireId}`,
    );

    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Création d'une annonce.
 *
 * Le payload doit notamment contenir :
 *
 * {
 *   nom,
 *   prenom,
 *   region,
 *   departement,
 *   club,
 *   passage_grade,
 *   kata,
 *   role_recherche,
 *   mobilite,
 *   disponibilites,
 *   content,
 *   tel,
 *   mail,
 *   contact_phone_public,
 *   contact_email_public,
 *   password
 * }
 */
export const createPartenaireKata = async (
  partenaireData,
) => {
  try {
    const response = await api.post(
      "",
      partenaireData,
    );

    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Vérifie l'accès à une annonce.
 *
 * credentials :
 *
 * {
 *   email,
 *   password
 * }
 */
export const verifyPartenaireKata = async (
  partenaireId,
  credentials,
) => {
  if (!partenaireId) {
    throw new Error(
      "L'identifiant du partenaire est obligatoire.",
    );
  }

  try {
    const response = await api.post(
      `/${partenaireId}/verify`,
      {
        credentials,
      },
    );

    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Modification d'une annonce.
 *
 * credentials :
 *
 * {
 *   email,
 *   password
 * }
 *
 * partenaireData contient uniquement les champs à modifier.
 *
 * Pour modifier le mot de passe :
 *
 * {
 *   new_password: "nouveau mot de passe"
 * }
 */
export const updatePartenaireKata = async (
  partenaireId,
  credentials,
  partenaireData,
) => {
  if (!partenaireId) {
    throw new Error(
      "L'identifiant du partenaire est obligatoire.",
    );
  }

  try {
    const response = await api.patch(
      `/${partenaireId}`,
      {
        credentials,
        data: partenaireData,
      },
    );

    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Suppression d'une annonce.
 *
 * credentials :
 *
 * {
 *   email,
 *   password
 * }
 */
export const deletePartenaireKata = async (
  partenaireId,
  credentials,
) => {
  if (!partenaireId) {
    throw new Error(
      "L'identifiant du partenaire est obligatoire.",
    );
  }

  try {
    const response = await api.delete(
      `/${partenaireId}`,
      {
        data: {
          credentials,
        },
      },
    );

    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/* =========================================================
   COMPATIBILITÉ AVEC L'ANCIEN FRONT LIGUE
========================================================= */

/**
 * Ancienne fonction du site Ligue.
 *
 * Contrairement à getPartenairesKata(), elle renvoie directement
 * un tableau et charge automatiquement toutes les pages.
 */
export const getAllPartenaires = async (
  params = {},
) => {
  const commonParams = cleanParams({
    ...params,
    limit: 48,
  });

  const firstPage = await getPartenairesKata({
    ...commonParams,
    page: 1,
  });

  const totalPages = Math.max(
    Number(firstPage?.meta?.total_pages) || 1,
    1,
  );

  if (totalPages === 1) {
    return firstPage.data;
  }

  const remainingPageRequests = Array.from(
    {
      length: totalPages - 1,
    },
    (_, index) =>
      getPartenairesKata({
        ...commonParams,
        page: index + 2,
      }),
  );

  const remainingPages = await Promise.all(
    remainingPageRequests,
  );

  return [
    ...firstPage.data,
    ...remainingPages.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : [],
    ),
  ];
};

/**
 * Ancien nom utilisé par le front Ligue.
 * Retourne directement l'annonce et non l'enveloppe API.
 */
export const getPartenaireById = async (
  partenaireId,
) => {
  const response = await getPartenaireKataById(
    partenaireId,
  );

  return response?.data || null;
};

/**
 * L'ancienne route spécifique au département n'existe plus.
 * Le département est maintenant envoyé en query param.
 */
export const getPartenaireByDepartement = async (
  departement,
  params = {},
) => {
  return getAllPartenaires({
    ...params,
    departement: normalizeDepartement(departement),
  });
};

/**
 * L'ancienne route spécifique à la région n'existe plus.
 * La région est maintenant envoyée en query param.
 */
export const getPartenaireByRegion = async (
  region,
  params = {},
) => {
  return getAllPartenaires({
    ...params,
    region: String(region || "").trim(),
  });
};

/**
 * Retourne directement l'objet des statistiques.
 */
export const getPartenairesStats = async () => {
  const response =
    await getPartenairesKataStats();

  return response.data;
};

/**
 * Alias conservant les anciens noms du site Ligue.
 */
export const createPartenaire =
  createPartenaireKata;

export const verifyPartenaireAccess =
  verifyPartenaireKata;

export const updatePartenaire =
  updatePartenaireKata;

export const deletePartenaire =
  deletePartenaireKata;

export default api;